import React, { useState, useEffect, useCallback } from 'react';
import logo from '../Logos/AlegraLogoBlanco.png';
import instagramLogo from '../Logos/logoInstragram.png';
import './Company.css';
import './Home.css';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, uploadImage } from '../firebase';
import Spinner from './Spinner';
import { Link } from 'react-router-dom';
import Subscription from './Subscription';
import { useEditMode } from '../contexts/EditModeContext';

const DEFAULT_TITLE = '';

function normalizeHomeDocData(raw) {
  if (!raw) {
    return { title: DEFAULT_TITLE, instagram: '', facebook: '', items: [] };
  }
  const title = raw.title || DEFAULT_TITLE;
  const instagram = raw.instagram || '';
  const facebook = raw.facebook || '';

  if (Array.isArray(raw.items) && raw.items.length > 0) {
    const items = raw.items.map((item, i) => ({
      ...item,
      id: item.id || `migrated-${Date.now()}-${i}`,
    }));
    return { title, instagram, facebook, items };
  }

  if (Array.isArray(raw.images) && raw.images.length > 0) {
    const items = raw.images
      .map((img) => {
        const url = typeof img === 'string' ? img : img?.url || '';
        if (!url) return null;
        return {
          id: Date.now() + Math.random(),
          type: 'image',
          url,
          link: typeof img === 'object' ? img.link || '' : '',
          tooltip: typeof img === 'object' ? img.tooltip || '' : '',
          caption: typeof img === 'object' ? img.caption || '' : '',
        };
      })
      .filter(Boolean);
    return { title, instagram, facebook, items };
  }

  return { title, instagram, facebook, items: [] };
}

async function loadHomeFromFirestore() {
  const docRef = doc(db, 'HomeInfo', 'main');
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) {
    return normalizeHomeDocData(null);
  }
  return normalizeHomeDocData(docSnap.data());
}

const Home = () => {
  const { isEditMode, isAdmin } = useEditMode();
  const canEditHome = isAdmin && isEditMode;

  const [homeData, setHomeData] = useState({
    title: DEFAULT_TITLE,
    instagram: '',
    facebook: '',
    items: [],
  });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    const run = async () => {
      setIsInitialLoading(true);
      try {
        const next = await loadHomeFromFirestore();
        setHomeData(next);
      } catch (error) {
        console.error('Error fetching home info:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };
    run();
  }, []);

  const handleDiscardChanges = async () => {
    if (!window.confirm('¿Descartar los cambios no guardados y volver a la última versión guardada?')) {
      return;
    }
    setIsDiscarding(true);
    try {
      const next = await loadHomeFromFirestore();
      setHomeData(next);
    } catch (error) {
      console.error('Error al descartar cambios:', error);
      alert('No se pudo recargar la información. Intenta de nuevo.');
    } finally {
      setIsDiscarding(false);
    }
  };

  const handleImageUpload = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const imageUrl = await uploadImage(file);
      setHomeData((prev) => {
        const newItems = [...prev.items];
        newItems[index] = {
          ...newItems[index],
          url: imageUrl,
          alt: file.name.replace(/\.[^/.]+$/, ''),
          uploadDate: new Date().toISOString(),
        };
        return { ...prev, items: newItems };
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(error.message || 'Error al subir la imagen. Por favor intente nuevamente.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddContent = (type) => {
    const newItem = { id: Date.now() + Math.random(), type };
    if (type === 'text') {
      Object.assign(newItem, {
        text: '',
        headerType: 'h2',
        fontType: 'conventional',
        color: '#ffffff',
        link: '',
        tooltip: '',
      });
    } else if (type === 'image') {
      Object.assign(newItem, { url: '', link: '', tooltip: '', caption: '' });
    } else if (type === 'video') {
      Object.assign(newItem, { url: '', title: '', description: '' });
    }
    setHomeData((prev) => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const handleRemoveContent = (index) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleVideoChange = (index, field, value) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const getYouTubeEmbedUrl = useCallback((url) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    return url;
  }, []);

  const handleTextChange = (index, value) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, text: value } : item)),
    }));
  };

  const handleTextHeaderChange = (index, value) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, headerType: value } : item)),
    }));
  };

  const handleTextFontChange = (index, value) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, fontType: value } : item)),
    }));
  };

  const handleTextColorChange = (index, value) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, color: value } : item)),
    }));
  };

  const handleTextLinkChange = (index, value) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, link: value } : item)),
    }));
  };

  const handleTextTooltipChange = (index, value) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, tooltip: value } : item)),
    }));
  };

  const handleImageLinkChange = (index, value) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, link: value } : item)),
    }));
  };

  const handleImageTooltipChange = (index, value) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, tooltip: value } : item)),
    }));
  };

  const handleImageCaptionChange = (index, value) => {
    setHomeData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, caption: value } : item)),
    }));
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const renderTextElement = (text, headerType, fontType, color, index, link, tooltip) => {
    const getFontStyle = () => {
      switch (fontType) {
        case 'elegant':
          return { fontFamily: "'Plus Jakarta Sans', sans-serif", fontStyle: 'italic' };
        case 'bold':
          return { fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: '700' };
        case 'italic':
          return { fontFamily: "'DM Sans', sans-serif", fontStyle: 'italic' };
        case 'conventional':
        default:
          return { fontFamily: "'Plus Jakarta Sans', sans-serif" };
      }
    };

    const textElement = (() => {
      const baseStyle = {
        color: color || '#ffffff',
        ...getFontStyle(),
      };
      const pStyle = { ...baseStyle, fontFamily: "'DM Sans', sans-serif" };

      switch (headerType) {
        case 'h1':
          return <h1 style={baseStyle}>{text}</h1>;
        case 'h2':
          return <h2 style={baseStyle}>{text}</h2>;
        case 'h3':
          return <h3 style={baseStyle}>{text}</h3>;
        case 'h4':
          return <h4 style={baseStyle}>{text}</h4>;
        case 'p':
          return <p style={pStyle}>{text}</p>;
        default:
          return <h2 style={baseStyle}>{text}</h2>;
      }
    })();

    if (link && isValidUrl(link)) {
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          title={tooltip || ''}
          style={{
            textDecoration: 'underline',
            cursor: 'pointer',
            color: color || '#ffffff',
          }}
        >
          {textElement}
        </a>
      );
    }

    return textElement;
  };

  const handleSocialChange = (e) => {
    const { name, value } = e.target;
    setHomeData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const cleanItems = homeData.items.map((item) => {
        const cleanItem = { ...item };
        if (cleanItem.text !== undefined) cleanItem.text = cleanItem.text ? cleanItem.text.trim() : '';
        if (cleanItem.title !== undefined) cleanItem.title = cleanItem.title ? cleanItem.title.trim() : '';
        if (cleanItem.description !== undefined) {
          cleanItem.description = cleanItem.description ? cleanItem.description.trim() : '';
        }
        if (cleanItem.url !== undefined) cleanItem.url = cleanItem.url || '';
        if (cleanItem.link !== undefined) cleanItem.link = cleanItem.link || '';
        if (cleanItem.tooltip !== undefined) cleanItem.tooltip = cleanItem.tooltip || '';
        if (cleanItem.caption !== undefined) cleanItem.caption = cleanItem.caption ? cleanItem.caption.trim() : '';
        if (cleanItem.headerType !== undefined) cleanItem.headerType = cleanItem.headerType || 'h2';
        if (cleanItem.fontType !== undefined) cleanItem.fontType = cleanItem.fontType || 'conventional';
        if (cleanItem.color !== undefined) cleanItem.color = cleanItem.color || '#ffffff';
        if (!cleanItem.id) cleanItem.id = Date.now() + Math.random();
        if (!cleanItem.type) cleanItem.type = 'text';
        return cleanItem;
      });

      const imagesFromItems = cleanItems.filter((i) => i.type === 'image' && i.url).map((i) => i.url);

      await setDoc(doc(db, 'HomeInfo', 'main'), {
        title: homeData.title.trim(),
        instagram: (homeData.instagram || '').trim(),
        facebook: (homeData.facebook || '').trim(),
        items: cleanItems,
        images: imagesFromItems,
        lastUpdated: new Date().toISOString(),
      });
      setHomeData((prev) => ({ ...prev, items: cleanItems }));
      alert('Cambios guardados correctamente.');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error al guardar los cambios, por favor intente nuevamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyLink = async () => {
    const shareUrl = window.location.href;
    const shareData = {
      title: homeData.title || 'Allegra Club',
      text: 'Te invito a conocer Allegra Club',
      url: shareUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        setCopySuccess('¡Gracias por compartir!');
        setTimeout(() => setCopySuccess(''), 2500);
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess('¡Enlace copiado!');
      setTimeout(() => setCopySuccess(''), 2500);
    } catch (err) {
      console.error('Error al copiar el enlace:', err);
      setCopySuccess('No se pudo copiar el enlace');
      setTimeout(() => setCopySuccess(''), 2500);
    }
  };

  if (isInitialLoading) {
    return <Spinner />;
  }

  return (
    <div
      className={`cyber-shell cyber-shell--wide home-page${canEditHome ? ' home-page--edit-active' : ''}`}
    >
      <div className="cyber-shell__frame" aria-hidden="true" />
      <div className="cyber-shell__body">
        <p className="cyber-shell__subtitle">Club house · Bell Ville · experiencias</p>
        <div className="home">
          <div className="home-logo-container">
            <Link to="/entradas">
              <img src={logo} alt="Logo" className="home-logo" />
            </Link>
          </div>
          {canEditHome ? (
            <div className="admin-title">
              <input
                type="text"
                value={homeData.title}
                onChange={(e) => setHomeData((prev) => ({ ...prev, title: e.target.value }))}
                className="title-input"
              />
            </div>
          ) : (
            <h1 className="cyber-shell__title cyber-shell__title--readable">{homeData.title}</h1>
          )}

          <div className="company-content home-company-content">
            {homeData.items &&
              Array.isArray(homeData.items) &&
              homeData.items.map((item, index) => (
                <div key={`item-${item.id}`} className={`company-item ${item.type}-item`}>
                  {canEditHome && (
                    <button
                      type="button"
                      onClick={() => handleRemoveContent(index)}
                      className="remove-button"
                      title={`Eliminar ${item.type === 'text' ? 'texto' : item.type === 'image' ? 'imagen' : 'video'}`}
                    >
                      ✕
                    </button>
                  )}

                  {item.type === 'text' && (
                    <>
                      {canEditHome ? (
                        <div className="text-edit-controls">
                          <textarea
                            value={item.text}
                            onChange={(e) => handleTextChange(index, e.target.value)}
                            placeholder="Escribe tu texto aquí..."
                            className="text-area"
                          />
                          <div className="text-format-controls">
                            <select
                              value={item.headerType || 'h2'}
                              onChange={(e) => handleTextHeaderChange(index, e.target.value)}
                              className="text-header-select"
                            >
                              <option value="h1">Título Principal (H1)</option>
                              <option value="h2">Título Secundario (H2)</option>
                              <option value="h3">Título Terciario (H3)</option>
                              <option value="p">Párrafo (P)</option>
                            </select>
                            <select
                              value={item.fontType || 'conventional'}
                              onChange={(e) => handleTextFontChange(index, e.target.value)}
                              className="text-font-select"
                            >
                              <option value="conventional">Convencional</option>
                              <option value="elegant">Elegante</option>
                              <option value="bold">Negrita</option>
                              <option value="italic">Cursiva</option>
                            </select>
                            <input
                              type="color"
                              value={item.color || '#ffffff'}
                              onChange={(e) => handleTextColorChange(index, e.target.value)}
                              className="text-color-input"
                              title="Seleccionar color"
                            />
                          </div>
                          <div className="text-link-controls">
                            <input
                              type="text"
                              placeholder="URL completa (ej: https://...)"
                              value={item.link || ''}
                              onChange={(e) => handleTextLinkChange(index, e.target.value)}
                              className={`text-link-input ${item.link && !isValidUrl(item.link) ? 'invalid-url' : ''}`}
                            />
                            {item.link && !isValidUrl(item.link) && (
                              <div className="text-url-error-message">URL inválida</div>
                            )}
                            <input
                              type="text"
                              placeholder="Tooltip (texto al hover)"
                              value={item.tooltip || ''}
                              onChange={(e) => handleTextTooltipChange(index, e.target.value)}
                              className="text-tooltip-input"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="company-text">
                          {renderTextElement(
                            item.text,
                            item.headerType || 'h2',
                            item.fontType || 'conventional',
                            item.color || '#ffffff',
                            index,
                            item.link || '',
                            item.tooltip || ''
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {item.type === 'image' && (
                    <>
                      {canEditHome ? (
                        <div className="image-edit-controls">
                          {!item.url && (
                            <div className="file-upload-area">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(index, e)}
                                className="file-input"
                                id={`home-file-${item.id}`}
                              />
                              <label htmlFor={`home-file-${item.id}`} className="file-label">
                                📁 Seleccionar Imagen
                              </label>
                            </div>
                          )}
                          {item.url && (
                            <div className="image-container">
                              <img
                                src={item.url}
                                alt={item.alt || 'Imagen'}
                                className="company-image"
                                loading="lazy"
                              />
                              <div className="centered-replace-button">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleImageUpload(index, e)}
                                  className="replace-image-input"
                                  id={`home-replace-${item.id}`}
                                  title="Reemplazar imagen"
                                />
                                <label htmlFor={`home-replace-${item.id}`} className="replace-image-label">
                                  🔄 Reemplazar Imagen
                                </label>
                              </div>
                              <input
                                type="text"
                                placeholder="URL completa (enlace al clic)"
                                value={item.link || ''}
                                onChange={(e) => handleImageLinkChange(index, e.target.value)}
                                className={`image-link-input ${item.link && !isValidUrl(item.link) ? 'invalid-url' : ''}`}
                              />
                              {item.link && !isValidUrl(item.link) && (
                                <div className="url-error-message">URL inválida</div>
                              )}
                              <input
                                type="text"
                                placeholder="Tooltip"
                                value={item.tooltip || ''}
                                onChange={(e) => handleImageTooltipChange(index, e.target.value)}
                                className="image-tooltip-input"
                              />
                              <input
                                type="text"
                                placeholder="Pie de foto"
                                value={item.caption || ''}
                                onChange={(e) => handleImageCaptionChange(index, e.target.value)}
                                className="image-caption-input"
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="company-image-container">
                          {item.link && isValidUrl(item.link) ? (
                            <>
                              <a href={item.link} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={item.url}
                                  alt={item.alt || 'Imagen'}
                                  className="company-image clickable"
                                  title={item.tooltip || ''}
                                  loading="lazy"
                                />
                              </a>
                              {item.caption && <span className="image-caption">{item.caption}</span>}
                            </>
                          ) : (
                            <>
                              <img
                                src={item.url}
                                alt={item.alt || 'Imagen'}
                                className="company-image"
                                title={item.tooltip || ''}
                                loading="lazy"
                              />
                              {item.caption && <span className="image-caption">{item.caption}</span>}
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {item.type === 'video' && (
                    <>
                      {canEditHome ? (
                        <div className="video-edit-controls">
                          <input
                            type="text"
                            placeholder="URL del video de YouTube"
                            value={item.url || ''}
                            onChange={(e) => handleVideoChange(index, 'url', e.target.value)}
                            className="video-url-input"
                          />
                          <input
                            type="text"
                            placeholder="Título del video"
                            value={item.title || ''}
                            onChange={(e) => handleVideoChange(index, 'title', e.target.value)}
                            className="video-title-input"
                          />
                          <textarea
                            placeholder="Descripción del video"
                            value={item.description || ''}
                            onChange={(e) => handleVideoChange(index, 'description', e.target.value)}
                            className="video-description-input"
                          />
                        </div>
                      ) : (
                        <div className="video-container">
                          {item.title && <h3 className="video-title">{item.title}</h3>}
                          {item.description && <p className="video-description">{item.description}</p>}
                          {item.url && (
                            <div className="video-embed">
                              <iframe
                                src={getYouTubeEmbedUrl(item.url)}
                                title={item.title || 'Video'}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
          </div>

          <div className="home-social">
            {canEditHome ? (
              <>
                <input
                  type="text"
                  name="instagram"
                  value={homeData.instagram}
                  onChange={handleSocialChange}
                  placeholder="Instagram URL"
                  className="social-input"
                />
                <input
                  type="text"
                  name="facebook"
                  value={homeData.facebook}
                  onChange={handleSocialChange}
                  placeholder="Facebook URL"
                  className="social-input"
                />
              </>
            ) : (
              <>
                <a href={homeData.instagram} target="_blank" rel="noopener noreferrer">
                  <img src={instagramLogo} alt="Instagram" className="social-logo" />
                </a>
              </>
            )}
          </div>
          <div className="home-subscription-wrap">
            <Subscription />
          </div>
          <div className="share-app-container">
            <button
              type="button"
              onClick={handleCopyLink}
              className="share-button"
              aria-label="Compartir aplicación"
            >
              <span className="share-button__glow" aria-hidden="true" />
              <span className="share-button__content">
                <svg
                  className="share-button__icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                <span className="share-button__label">Compartir App</span>
              </span>
            </button>
            <p
              className={`copy-success${copySuccess ? ' copy-success--visible' : ''}`}
              role="status"
              aria-live="polite"
            >
              {copySuccess}
            </p>
          </div>
        </div>
      </div>

      {canEditHome && (
        <div className="floating-admin-controls-container" aria-label="Controles de edición del inicio">
          <div className="admin-controls-group">
            <div className="admin-controls">
              <button type="button" onClick={() => handleAddContent('text')}>
                📝 Agregar Texto
              </button>
              <button type="button" onClick={() => handleAddContent('image')}>
                🖼️ Agregar Imagen
              </button>
              <button type="button" onClick={() => handleAddContent('video')}>
                🎥 Agregar Video
              </button>
            </div>
            <div className="admin-controls-actions">
              <button
                type="button"
                className="floating-discard-button"
                onClick={handleDiscardChanges}
                disabled={isSaving || isDiscarding}
                title="Descartar cambios y recargar desde el servidor"
              >
                {isDiscarding ? '↩️ Recargando...' : '↩️ Descartar'}
              </button>
              <button
                type="button"
                className="floating-save-button"
                onClick={handleSaveChanges}
                disabled={isSaving || isDiscarding}
                title={isSaving ? 'Guardando...' : 'Guardar todos los cambios'}
              >
                {isSaving ? '💾 Guardando...' : '💾 Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isUploading && (
        <div className="loading-overlay">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default Home;
